import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Button, Card, Notice } from '@ailab/ui';
import { adminUserDetailSchema, revealedEmailSchema } from '@ailab/contracts';
import { apiRequest, errorMessage } from '../../lib/http';
import { describeMovement, MOVEMENT_LABELS } from '../account/BalanceSection';
import styles from '../account/account.module.css';

// Ficha de usuario (RF-08.03): datos, acceso, saldo, movimientos, condiciones y auditoría.
// Solo lectura en A-14; los cambios se hacen con las rutas de administración ya auditadas.

const credits = new Intl.NumberFormat('es-ES');
const when = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });
const date = (value: string | null) => (value ? when.format(new Date(value)) : 'nunca');

const PLANS: Record<string, string> = {
  unassigned: 'Sin asignar',
  monthly: 'Mensual',
  annual: 'Anual',
  lifetime: 'De por vida',
};

export function AdminUserPage() {
  const { userId } = useParams({ from: '/admin/usuarios/$userId' });
  const detail = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => apiRequest(`/admin/users/${userId}`, adminUserDetailSchema),
  });
  const [email, setEmail] = useState<string>();
  const [revealError, setRevealError] = useState<string>();

  async function reveal() {
    setRevealError(undefined);
    try {
      const shown = await apiRequest(`/admin/users/${userId}/email/reveal`, revealedEmailSchema, {
        method: 'POST',
      });
      setEmail(shown.email);
    } catch (failure) {
      setRevealError(errorMessage(failure));
    }
  }

  if (detail.isPending) return <p role="status">Cargando la ficha…</p>;
  if (detail.isError) return <Notice tone="error">{errorMessage(detail.error)}</Notice>;
  const { user, access, balance, movements, acceptances, pendingLegal, auditEvents } = detail.data;

  return (
    <div className={styles.sections}>
      <p>
        <Link className={styles.inlineLink} to="/admin/usuarios">
          Volver a usuarios
        </Link>
      </p>
      <h1>
        {user.firstName} {user.lastName}
      </h1>

      <Card>
        <h2 className={styles.sectionTitle}>Datos</h2>
        <dl className={styles.balances}>
          <div>
            <dt>Email</dt>
            <dd>
              {email ?? user.emailMasked}{' '}
              {email ? null : (
                <Button type="button" variant="ghost" onClick={() => void reveal()}>
                  Mostrar (queda registrado)
                </Button>
              )}
            </dd>
          </div>
          <div>
            <dt>Nombre en Skool</dt>
            <dd>{user.skoolName}</dd>
          </div>
          <div>
            <dt>Estado</dt>
            <dd>{user.status === 'active' ? 'Activa' : 'Desactivada'}</dd>
          </div>
          <div>
            <dt>Verificación en dos pasos</dt>
            <dd>{user.mfaEnabled ? 'Activada' : 'No activada'}</dd>
          </div>
          <div>
            <dt>Alta y último acceso</dt>
            <dd>
              {date(user.createdAt)} · {date(user.lastLoginAt)}
            </dd>
          </div>
        </dl>
        {revealError ? <Notice tone="error">{revealError}</Notice> : null}
      </Card>

      <Card>
        <h2 className={styles.sectionTitle}>Acceso y membresía</h2>
        {access.blocked ? (
          <Notice tone="error">Bloqueada: {access.blockReason ?? 'sin motivo'}</Notice>
        ) : null}
        <p>Roles: {access.roles.length ? access.roles.join(', ') : 'ninguno'}</p>
        <p>
          Plan: {PLANS[access.membership.plan] ?? access.membership.plan}
          {access.membership.renewalOn ? ` · renueva el ${access.membership.renewalOn}` : ''}
        </p>
      </Card>

      <Card>
        <h2 className={styles.sectionTitle}>Saldo</h2>
        <dl className={styles.balances}>
          <div>
            <dt>Disponible</dt>
            <dd>{credits.format(balance.available)} créditos</dd>
          </div>
          <div>
            <dt>Retenido</dt>
            <dd>{credits.format(balance.held)} créditos</dd>
          </div>
          <div>
            <dt>Bloqueado por membresía</dt>
            <dd>{credits.format(balance.frozen)} créditos</dd>
          </div>
        </dl>
        <h3 className={styles.subTitle}>Últimos movimientos</h3>
        {movements.length === 0 ? <p>Todavía no hay movimientos.</p> : null}
        <ul className={styles.sessionList}>
          {movements.map((movement) => (
            <li key={movement.id} className={styles.session}>
              <div>
                <strong>{MOVEMENT_LABELS[movement.type]}</strong>
                <div className={styles.sessionMeta}>
                  {date(movement.createdAt)}
                  {movement.reason ? ` · ${movement.reason}` : ''}
                </div>
              </div>
              <span className={styles.amount}>{describeMovement(movement)}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <h2 className={styles.sectionTitle}>Condiciones</h2>
        {pendingLegal.length ? (
          <Notice tone="info">
            Pendiente de aceptar: {pendingLegal.map((doc) => doc.version).join(', ')}
          </Notice>
        ) : null}
        <ul className={styles.sessionList}>
          {acceptances.map((row) => (
            <li key={`${row.kind}-${row.version}`} className={styles.session}>
              <div>
                <strong>
                  {row.title} ({row.version})
                </strong>
                <div className={styles.sessionMeta}>
                  {date(row.acceptedAt)} · {row.source === 'signup' ? 'en el alta' : row.source}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <h2 className={styles.sectionTitle}>Auditoría</h2>
        {auditEvents.length === 0 ? <p>Sin eventos.</p> : null}
        <ul className={styles.sessionList}>
          {auditEvents.map((event) => (
            <li key={event.id} className={styles.session}>
              <div>
                <strong>{event.action}</strong>
                <div className={styles.sessionMeta}>
                  {date(event.occurredAt)}
                  {event.reason ? ` · ${event.reason}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
